import React, { useState, useMemo } from "react"
import HeaderSection from "../../components/header-section"
import Sidebar from "../../features/sidebar/components/sidebar"
import {
  useGetPendingResultsApprovalsQuery,
  useApproveOrganizationResultsMutation,
  PendingResultsApproval,
} from "../../app/services/meseeknaApiV2"
import { MdOutlineApproval, MdCheck, MdClose, MdInfo, MdSearch } from "react-icons/md"
import { FiClock, FiUsers } from "react-icons/fi"
import { HiOutlineOfficeBuilding } from "react-icons/hi"
import { IconBaseProps, IconType } from "react-icons"
import { useNavigate } from "react-router-dom"

// Helper to create properly typed icon components from react-icons
// react-icons IconType returns ReactNode which is incompatible with JSX component requirements
const createIcon = (Icon: IconType): React.FC<IconBaseProps> => {
  return (props) => Icon(props) as React.ReactElement
}

const ApprovalIcon = createIcon(MdOutlineApproval)
const CheckIcon = createIcon(MdCheck)
const CloseIcon = createIcon(MdClose)
const InfoIcon = createIcon(MdInfo)
const ClockIcon = createIcon(FiClock)
const UsersIcon = createIcon(FiUsers)
const BuildingIcon = createIcon(HiOutlineOfficeBuilding)
const SearchIcon = createIcon(MdSearch)

const getStatusBadge = (approval: PendingResultsApproval) => {
  const { resultsApprovalStatus, completedUserCount, totalUserCount } = approval
  const allCompleted = totalUserCount > 0 && completedUserCount === totalUserCount

  if (resultsApprovalStatus === "approved") {
    return (
      <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
        Approved
      </span>
    )
  }
  if (resultsApprovalStatus === "rejected") {
    return (
      <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
        Rejected
      </span>
    )
  }
  if (resultsApprovalStatus === "pending") {
    return (
      <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
        Pending Approval
      </span>
    )
  }
  // No approval status set yet
  if (allCompleted) {
    return (
      <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
        Ready for Approval
      </span>
    )
  }
  return (
    <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
      In Progress
    </span>
  )
}

const getStatusDescription = (approval: PendingResultsApproval) => {
  const { resultsApprovalStatus, completedUserCount, totalUserCount } = approval
  const allCompleted = totalUserCount > 0 && completedUserCount === totalUserCount

  if (resultsApprovalStatus === "approved") {
    return "Results have been approved and are visible to the organization"
  }
  if (resultsApprovalStatus === "rejected") {
    return "Results were rejected - organization may request re-approval"
  }
  if (resultsApprovalStatus === "pending") {
    return "All users have completed their simulations - awaiting approval"
  }
  if (allCompleted) {
    return "All users have completed - ready for approval"
  }
  return `${completedUserCount} of ${totalUserCount} users have completed simulations`
}

const ApprovalCard: React.FC<{
  approval: PendingResultsApproval
  onApprove: (id: number) => void
  onReject: (id: number) => void
  isApproving: boolean
  isRejecting: boolean
}> = ({ approval, onApprove, onReject, isApproving, isRejecting }) => {
  const navigate = useNavigate()

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "N/A"
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const completionPercentage =
    approval.totalUserCount > 0
      ? Math.round((approval.completedUserCount / approval.totalUserCount) * 100)
      : 0

  const isApproved = approval.resultsApprovalStatus === "approved"
  const canApprove = approval.resultsApprovalStatus !== "approved"

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900">
            {approval.organizationName}
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            {getStatusDescription(approval)}
          </p>
        </div>
        {getStatusBadge(approval)}
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
        <div className="flex items-center gap-2 text-gray-600">
          <UsersIcon size={14} />
          <span>
            {approval.completedUserCount} of {approval.totalUserCount} users
            completed
          </span>
        </div>
        <div className="flex items-center gap-2 text-gray-600">
          <BuildingIcon size={14} />
          <span>Organization ID: {approval.id}</span>
        </div>
        <div className="flex items-center gap-2 text-gray-600">
          <ClockIcon size={14} />
          <span>
            Completed: {formatDate(approval.allUsersCompletedAt)}
          </span>
        </div>
        <div className="flex items-center gap-2 text-gray-600">
          <InfoIcon size={14} />
          <span>Requested: {formatDate(approval.resultsApprovalRequestedAt)}</span>
        </div>
      </div>

      <div className="mb-4">
        <div className="flex justify-between text-xs text-gray-600 mb-1">
          <span>Completion Progress</span>
          <span>{completionPercentage}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all ${
              completionPercentage === 100 ? "bg-green-600" : "bg-blue-600"
            }`}
            style={{ width: `${completionPercentage}%` }}
          />
        </div>
      </div>

      <div className="flex gap-3">
        <button
          onClick={() => navigate(`/dashboard/super-admin/org-reports?orgId=${approval.id}`)}
          className="flex-1 px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors flex items-center justify-center gap-2"
        >
          <InfoIcon size={16} />
          Review Results
        </button>
        {canApprove && (
          <>
            <button
              onClick={() => onApprove(approval.id)}
              disabled={isApproving || isRejecting}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {isApproving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Approving...
                </>
              ) : (
                <>
                  <CheckIcon size={16} />
                  Approve
                </>
              )}
            </button>
            <button
              onClick={() => onReject(approval.id)}
              disabled={isApproving || isRejecting}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {isRejecting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Rejecting...
                </>
              ) : (
                <>
                  <CloseIcon size={16} />
                  Reject
                </>
              )}
            </button>
          </>
        )}
        {isApproved && (
          <span className="px-4 py-2 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
            <CheckIcon size={16} />
            Already Approved
          </span>
        )}
      </div>
    </div>
  )
}

const PendingResultsApprovalsPage: React.FC = () => {
  const [approvingId, setApprovingId] = useState<number | null>(null)
  const [rejectingId, setRejectingId] = useState<number | null>(null)
  const [searchQuery, setSearchQuery] = useState("")

  const {
    data: approvalsData,
    isLoading,
    error,
    refetch,
  } = useGetPendingResultsApprovalsQuery({ includeAll: true })

  const [approveResults] = useApproveOrganizationResultsMutation()

  const handleApprove = async (organizationId: number) => {
    setApprovingId(organizationId)
    try {
      await approveResults({ organizationId, approve: true }).unwrap()
      refetch()
    } catch (error) {
      console.error("Error approving results:", error)
      alert("Failed to approve results. Please try again.")
    } finally {
      setApprovingId(null)
    }
  }

  const handleReject = async (organizationId: number) => {
    if (
      !window.confirm(
        "Are you sure you want to reject these results? The recruiter admin will need to request approval again.",
      )
    ) {
      return
    }

    setRejectingId(organizationId)
    try {
      await approveResults({ organizationId, approve: false }).unwrap()
      refetch()
    } catch (error) {
      console.error("Error rejecting results:", error)
      alert("Failed to reject results. Please try again.")
    } finally {
      setRejectingId(null)
    }
  }

  // Filter and sort organizations
  const sortedApprovals = useMemo(() => {
    const approvals = approvalsData?.data || []

    // Filter by search query
    const filtered = searchQuery
      ? approvals.filter((a) =>
          a.organizationName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          a.id.toString().includes(searchQuery)
        )
      : approvals

    // Sort: completed orgs first, then by completion percentage, then by name
    return [...filtered].sort((a, b) => {
      const aCompleted = a.totalUserCount > 0 && a.completedUserCount === a.totalUserCount
      const bCompleted = b.totalUserCount > 0 && b.completedUserCount === b.totalUserCount

      // Completed organizations first
      if (aCompleted && !bCompleted) return -1
      if (!aCompleted && bCompleted) return 1

      // Then by completion percentage (descending)
      const aPercent = a.totalUserCount > 0 ? a.completedUserCount / a.totalUserCount : 0
      const bPercent = b.totalUserCount > 0 ? b.completedUserCount / b.totalUserCount : 0
      if (aPercent !== bPercent) return bPercent - aPercent

      // Then alphabetically by name
      return a.organizationName.localeCompare(b.organizationName)
    })
  }, [approvalsData?.data, searchQuery])

  const completedCount = sortedApprovals.filter(
    (a) => a.totalUserCount > 0 && a.completedUserCount === a.totalUserCount
  ).length

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <HeaderSection />
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto">
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-2">
                <ApprovalIcon size={24} className="text-blue-600" />
                <h1 className="text-2xl font-bold text-gray-900">
                  Results Approval
                </h1>
              </div>
              <p className="text-gray-600">
                Review and approve organization results access. Organizations with completed simulations are shown first.
              </p>
            </div>

            {/* Search and Stats */}
            <div className="mb-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              <div className="relative flex-1 max-w-md">
                <SearchIcon
                  size={20}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  placeholder="Search organizations by name or ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="text-sm text-gray-600">
                {sortedApprovals.length} organizations
                {completedCount > 0 && (
                  <span className="ml-2 text-green-600 font-medium">
                    ({completedCount} completed)
                  </span>
                )}
              </div>
            </div>

            {isLoading && (
              <div className="flex justify-center items-center py-12">
                <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <p className="text-red-800">
                  Error loading organizations. Please try again.
                </p>
              </div>
            )}

            {!isLoading && !error && sortedApprovals.length === 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                <ApprovalIcon size={48} className="text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {searchQuery ? "No Matching Organizations" : "No Organizations Found"}
                </h3>
                <p className="text-gray-600">
                  {searchQuery
                    ? "Try adjusting your search criteria."
                    : "No organizations are available."}
                </p>
              </div>
            )}

            {!isLoading && !error && sortedApprovals.length > 0 && (
              <div className="space-y-4">
                {sortedApprovals.map((approval) => (
                  <ApprovalCard
                    key={approval.id}
                    approval={approval}
                    onApprove={handleApprove}
                    onReject={handleReject}
                    isApproving={approvingId === approval.id}
                    isRejecting={rejectingId === approval.id}
                  />
                ))}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  )
}

export default PendingResultsApprovalsPage
